import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import PAImage from "component/design-system/images/PAImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import ProductDescription from "modules/product/ProductDescription";
import dynamic from "next/dynamic";
import { renderHTML } from "util/DomHelper";
import HideIf from "util/HideIf";
import { getApparelTabs } from "util/apparel-helper";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import styles from "./apparel-component.module.scss";

const SizeChart = dynamic(() => import(/* webpackChunkName: "size-chart" */ "../V2/overview/overview-sections/SizeChart"));
const DynamicCmsContent = dynamic(() => import(/* webpackChunkName: "dynamic-cms-content" */ "../V2/overview/overview-sections/DynamicCmsContent"));

interface IProps {
  showApparelSection: string;
  setShowApparelSection: (section: string) => void;
  product: ICompactProduct;
  links: any;
}

interface ISpec {
  name: string;
  iconImage: string;
  description: string;
  uiDetails: {
    content: {
      name: string;
      iconImage: string;
      description: string;
      article: string;
    };
  };
}

interface IButtonProps {
  section: {
    id: string;
    title: string;
  };
  currentSection: string;
  onClick: (section: string) => void;
}

const OverviewButton = ({ section, currentSection, onClick }: IButtonProps) => {
  const isSelected = currentSection === section.id ? styles.overview_button_selected : "";

  const handleButtonClick = () => {
    onClick(section.id);
  };

  return (
    <button
      type="button"
      onClick={handleButtonClick}
      className={`${styles.overview_button} ${isSelected}`}
    >
      {section.title}
    </button>
  );
};

const ApparelOverview = ({
  showApparelSection, setShowApparelSection, links, product,
}: IProps) => {
  const {
    sizeChart, productSpecification, careGuide, dimensions, handling, productDescription,
  } = links;
  const overviewSections = getApparelTabs(links);
  const currentSection = overviewSections.some((section) => section.id === showApparelSection)
    ? showApparelSection
    : overviewSections[0]?.id;

  if (!overviewSections.length) {
    return null;
  }

  return (
    <div
      className={`${styles.apparel_overview_container}`}
      id="apparel_overview_container"
    >
      <div className={styles.apparel_overview_buttons}>
        {overviewSections.map((section) => (
          <OverviewButton
            key={section.id}
            section={section}
            currentSection={currentSection}
            onClick={setShowApparelSection}
          />
        ))}
      </div>
      <hr className={styles.specifications_hr} />
      <HideIf show={currentSection === "product_description" && productDescription?.length}>
        <div className={`${styles.apparel_tab_content} m-t-30`}>
          <ProductDescription productDescription={productDescription} sku={product.sku} />
        </div>
      </HideIf>

      <HideIf show={currentSection === "overview" && productSpecification?.length}>
        {" "}
        <div className={`${styles.overview_specifications} m-t-30`}>
          <div>
            <PAText bold className="m-b-15" fontSize={FONT_SIZE[2]} color="black">
              Product Highlights
            </PAText>

            <div className={styles.specifications_list}>
              {productSpecification?.map(
                (spec: ISpec) => {
                  const {
                    name, description, iconImage, article,
                  } = spec.uiDetails.content;
                  return (
                    <div key={spec.name}>
                      <div className={styles.spec_title_row}>
                        <PAImage
                          width={25}
                          height={25}
                          src={getRelativeImagePath(`/${iconImage}`)}
                          fallBackImage={getRelativeImagePath(`/cms/${iconImage}`)}
                          alt={name}
                        />
                        <div className={styles.spec_description}>
                          <PAText bold>
                            {name}
                          </PAText>

                          <PAText fontSize={FONT_SIZE[0]}>
                            {description}
                          </PAText>

                          <div dangerouslySetInnerHTML={renderHTML(article)} />
                        </div>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </div>
          <HideIf show={productDescription?.length}>
            <div>
              <PAText bold className="m-b-15" fontSize={FONT_SIZE[2]} color="black">
                Product Description
              </PAText>
              <ProductDescription productDescription={productDescription} sku={product.sku} />
            </div>
          </HideIf>
        </div>
      </HideIf>

      <HideIf show={currentSection === "size_chart"}>
        <SizeChart sizeChart={sizeChart} />
      </HideIf>

      <HideIf show={currentSection === "care_guide"}>
        <DynamicCmsContent dynamicContent={careGuide} />
      </HideIf>
      <HideIf show={currentSection === "dimensions"}>
        <DynamicCmsContent
          dynamicContent={dimensions}
          imgWidth={500}
          imgHeight={605}
          className="justify-content-around"
        />
      </HideIf>
      <HideIf show={currentSection === "handling"}>
        <DynamicCmsContent dynamicContent={handling} imgWidth={100} imgHeight={100} />
      </HideIf>
    </div>
  );
};

export default ApparelOverview;
